/**
 * P0-avviksregler. Tersklene er standardinnstillinger, ikke avtalte driftsgrenser.
 * Et datakvalitetsavvik stanser et misvisende effektivitetsvarsel.
 */
import type { Dataset, Deviation, SourceSync } from '../domain/types';
import { hoursBetween } from '../domain/time';
import type { PeriodResult } from './engine';
import { compareMetric } from './compare';

export interface DeviationThresholds {
  dieselPerTonneIncreasePct: number;
  idleSharePct: number;
}

export const DEFAULT_THRESHOLDS: DeviationThresholds = { dieselPerTonneIncreasePct: 20, idleSharePct: 25 };

function period(r: PeriodResult) {
  return { fromUtc: r.fromUtc, toUtc: r.toUtc, fromDate: r.query.fromDate, toDate: r.query.toDate };
}

function fmt(v: number, d: number) {
  return v.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d });
}

/**
 * Terskeltest med flyttallstoleranse. 1,50 h / 6,00 h × 100 blir 25,000000000000004 i IEEE 754;
 * det skal IKKE regnes som «over 25 %». En verdi må overstige terskelen med mer enn 1e-9 for å utløse regelen.
 */
export const THRESHOLD_EPSILON = 1e-9;
export function exceedsThreshold(value: number | null | undefined, threshold: number): boolean {
  if (value === null || value === undefined || !Number.isFinite(value)) return false;
  return value - threshold > THRESHOLD_EPSILON;
}

export function evaluateDeviations(
  ds: Dataset,
  current: PeriodResult,
  reference: PeriodResult | null,
  thresholds: DeviationThresholds = DEFAULT_THRESHOLDS,
  frozenNow: string = ds.frozenNow,
): Deviation[] {
  const out: Deviation[] = [];
  const scenarioId = current.query.scenarioId;
  const base = `${scenarioId}:${current.query.fromDate}:${current.query.toDate}`;
  const scope = { siteId: current.query.siteId, lineId: current.query.lineId };
  const periodLabel = current.query.fromDate === current.query.toDate ? current.query.fromDate : `${current.query.fromDate}–${current.query.toDate}`;

  /* R3: datakvalitet – manglende felt i beregningsområdet */
  const missingWarnings = current.warnings.filter((w) => w.code === 'MISSING_FUEL' || w.code === 'MISSING_ENGINE' || w.code === 'MISSING_IDLE');
  const dataQualityIssue = missingWarnings.length > 0;
  for (const w of missingWarnings) {
    out.push({
      id: `${base}:R3:${w.field}:${w.machineId ?? 'alle'}`,
      ruleId: 'R3_DATA_QUALITY',
      ruleLabel: 'Manglende forventet kilde/felt',
      severity: 'high',
      scope: { ...scope, machineId: w.machineId, sourceId: 'maskindata' },
      period: period(current),
      observed: `${w.recordIds?.length ?? 0} intervall mangler ${w.field}`,
      observation: w.message,
      possibleExplanation:
        'Telematikkenheten har ikke levert feltet for intervallet, eller synk fra maskinkilden er forsinket. Produksjonstallene er komplette og vises fortsatt.',
      nextCheck: `Kontroller kildeposten(e) ${w.recordIds?.slice(0, 3).join(', ')} i Datakilder, og vent på ny synk før effektivitetstall for ${periodLabel} brukes. Effektivitetsvarsler (R1) er stanset for perioden.`,
      evidenceRecordIds: w.recordIds ?? [],
      scenarioId,
      suppresses: ['R1_DIESEL_PER_TONNE'],
    });
  }

  /* R3: kilde forsinket mot konfigurert frekvens */
  for (const s of ds.sources) {
    if (s.isContextOnly || !s.expectedCadenceMinutes || !s.succeededAt) continue;
    const ageMin = (new Date(frozenNow).getTime() - new Date(s.succeededAt).getTime()) / 60000;
    if (ageMin > s.expectedCadenceMinutes * 1.5 || s.status === 'delayed') {
      out.push({
        id: `${scenarioId}:R3:source:${s.sourceId}`,
        ruleId: 'R3_DATA_QUALITY',
        ruleLabel: 'Forsinket kilde',
        severity: 'medium',
        scope: { ...scope, sourceId: s.sourceId },
        period: period(current),
        observed: `Siste vellykkede synk ${Math.round(ageMin / 60)} t siden (forventet hver ${s.expectedCadenceMinutes} min)`,
        observation: `${s.name}: siste vellykkede synk er eldre enn konfigurert forventet frekvens (${s.expectedCadenceMinutes} min). Nyeste intervaller kan mangle.`,
        possibleExplanation: 'Simulert forsinkelse i maskinkilden. I pilot kan dette skyldes nettverk, abonnement eller at enheten ikke sender data.',
        nextCheck: 'Se Datakilder → Maskindata og kjør «Prøv igjen» (simulert). Ikke bruk forholdstall for berørte intervaller før data er komplette.',
        evidenceRecordIds: [],
        scenarioId,
      });
    }
  }

  /* R1: diesel per tonn opp mer enn terskel mot komplett referanse */
  if (reference) {
    const cmp = compareMetric(current.dieselPerTonne, reference.dieselPerTonne);
    if (dataQualityIssue || !current.basisComplete) {
      // stanset av datakvalitetsavvik
    } else if (cmp.status === 'ok' && cmp.pctChange !== null && reference.basisComplete && exceedsThreshold(cmp.pctChange, thresholds.dieselPerTonneIncreasePct)) {
      const topFuel = [...current.machines].filter((m) => m.inScope).sort((a, b) => b.fuelLitres - a.fuelLitres).slice(0, 3);
      out.push({
        id: `${base}:R1`,
        ruleId: 'R1_DIESEL_PER_TONNE',
        ruleLabel: `Diesel per tonn opp > ${thresholds.dieselPerTonneIncreasePct} %`,
        severity: 'high',
        scope,
        period: period(current),
        reference: period(reference),
        observed: `${fmt(current.dieselPerTonne.value!, 3)} L/t mot ${fmt(reference.dieselPerTonne.value!, 3)} L/t (+${fmt(cmp.pctChange, 1)} %)`,
        observation: `Diesel per tonn er ${fmt(current.dieselPerTonne.value!, 3)} L/t (${fmt(current.fuelLitres.value!, 0)} L / ${fmt(current.tonnes.value!, 0)} t) mot ${fmt(reference.dieselPerTonne.value!, 3)} L/t i referanseperioden. Produsert mengde endret seg ${fmt((current.tonnes.value! / reference.tonnes.value! - 1) * 100, 1)} % og tilordnet diesel ${fmt((current.fuelLitres.value! / reference.fuelLitres.value! - 1) * 100, 1)} %.`,
        possibleExplanation: `Størst bidrag til diesel: ${topFuel.map((m) => `${m.machineId} (${fmt(m.fuelLitres, 0)} L)`).join(', ')}. Tomgangsandelen er ${current.idleShare.value !== null ? fmt(current.idleShare.value, 1) + ' %' : 'utilgjengelig'} og registrert stopptid ${fmt(current.stopHours.value!, 1)} h. Dette er bidrag som sammenfaller i tid – ikke en beregnet årsak.`,
        nextCheck: 'Åpne maskindetalj for maskinene med størst tomgang og sammenstill tomgangsintervallene med registrerte stopp i knuseverket. Vurder tiltakssimulering for tomgang.',
        evidenceRecordIds: [...(current.dieselPerTonne.numerator?.recordIds ?? []).slice(0, 20), ...(current.dieselPerTonne.denominator?.recordIds ?? [])],
        scenarioId,
      });
    }
  }

  /* R2: tomgangsandel over terskel (samlet og per maskin) */
  if (current.idleShare.status === 'ok' && exceedsThreshold(current.idleShare.value, thresholds.idleSharePct)) {
    out.push({
      id: `${base}:R2`,
      ruleId: 'R2_IDLE_SHARE',
      ruleLabel: `Tomgangsandel > ${thresholds.idleSharePct} %`,
      severity: 'medium',
      scope,
      period: period(current),
      observed: `${fmt(current.idleShare.value!, 1)} % (${fmt(current.idleHours.value!, 1)} h av ${fmt(current.engineHours.value!, 1)} h)`,
      observation: `Samlet tomgangsandel i produksjonsområdet er ${fmt(current.idleShare.value!, 1)} % (${fmt(current.idleHours.value!, 1)} tomgangstimer av ${fmt(current.engineHours.value!, 1)} motortimer).`,
      possibleExplanation: 'Høy tomgang kan sammenfalle med stopp i knuseverket (maskiner venter med motor på). Se maskiner med størst tomgang under.',
      nextCheck: 'Åpne maskiner med høyest tomgangsandel og kontroller tidsintervallene bak tallet mot registrerte stopp.',
      evidenceRecordIds: current.idleShare.numerator?.recordIds.slice(0, 20) ?? [],
      scenarioId,
    });
  }
  for (const m of current.machines) {
    if (!m.inScope || m.idleShare === null || !exceedsThreshold(m.idleShare, thresholds.idleSharePct)) continue;
    const idleHeavy = current.allocated.filter((a) => a.interval.machineId === m.machineId && a.idleHours !== null && a.engineHours !== null && a.engineHours > 0 && a.idleHours / a.engineHours > 0.5);
    const stopsOverlap = current.stops.filter((s) => idleHeavy.some((a) => a.interval.fromUtc < s.toUtc && s.fromUtc < a.interval.toUtc));
    out.push({
      id: `${base}:R2:${m.machineId}`,
      ruleId: 'R2_IDLE_SHARE',
      ruleLabel: `Tomgangsandel > ${thresholds.idleSharePct} % (maskin)`,
      severity: exceedsThreshold(m.idleShare, thresholds.idleSharePct * 2) ? 'high' : 'medium',
      scope: { ...scope, machineId: m.machineId },
      period: period(current),
      observed: `${fmt(m.idleShare, 1)} % (${fmt(m.idleHours, 2)} h av ${fmt(m.engineHours, 2)} h)`,
      observation: `${m.machineId} (${m.name}) har tomgangsandel ${fmt(m.idleShare, 1)} %: ${fmt(m.idleHours, 2)} tomgangstimer av ${fmt(m.engineHours, 2)} motortimer.`,
      possibleExplanation: stopsOverlap.length
        ? `Tomgangen sammenfaller i tid med ${stopsOverlap.length} registrerte stopp i knuseverket (${stopsOverlap.map((s) => s.stopReason ?? 'stopp').join('; ')}). Samtidighet er dokumentert, årsak er ikke.`
        : 'Ingen registrerte stopp i perioden; tomgangen må forklares av andre forhold.',
      nextCheck: `Åpne maskindetalj for ${m.machineId}, klikk tomgangstallet og sammenstill intervallene med stopptidslinjen.`,
      evidenceRecordIds: m.intervalIds,
      scenarioId,
    });
  }

  const order = { high: 0, medium: 1, info: 2 };
  return out.sort((a, b) => order[a.severity] - order[b.severity] || a.id.localeCompare(b.id));
}

export function sourceIsDelayed(s: SourceSync, frozenNow: string): boolean {
  if (!s.expectedCadenceMinutes || !s.succeededAt) return false;
  return hoursBetween(s.succeededAt, frozenNow) * 60 > s.expectedCadenceMinutes * 1.5;
}
